import React, { useState, useEffect } from 'react';
import { coursesAPI } from '../lib/api';
import { VideoCard } from '../components/VideoCard';
import { Loader2 } from 'lucide-react';
import { CoursesPageSEO } from '../components/SEO';

export const CoursesPage = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const response = await coursesAPI.getAll();
                // Handle paginated response
                setCourses(response.data.data || response.data);
            } catch (err) {
                setError('Failed to load courses');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchCourses();
    }, []);

    if (loading) {
        return (
            <>
                <CoursesPageSEO />
                <div className="min-h-[50vh] flex items-center justify-center" data-testid="courses-loading">
                    <Loader2 className="h-8 w-8 animate-spin text-accent" />
                </div>
            </>
        );
    }

    return (
        <div className="section-gap" data-testid="courses-page">
            <CoursesPageSEO />
            <div className="container-main">
                {/* Header */}
                <div className="mb-12">
                    <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                        Multi-session instruction
                    </span>
                    <h1 className="font-serif text-4xl md:text-5xl font-semibold text-primary mt-2">
                        Online Art Courses
                    </h1>
                    <p className="text-muted-foreground mt-4 max-w-2xl">
                        Multi-session courses in portrait drawing, oil painting, figure drawing, and classical techniques from professional artists. 
                        Each course includes structured lessons designed to build real skill over time.
                    </p>
                </div>

                {error && (
                    <div className="text-center py-12" data-testid="courses-error">
                        <p className="text-destructive">{error}</p>
                    </div>
                )}

                {!error && courses.length === 0 && (
                    <div className="text-center py-12" data-testid="courses-empty">
                        <p className="text-muted-foreground">No courses available at this time.</p>
                    </div>
                )}

                {/* Course Grid */}
                {courses.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="courses-grid">
                        {courses.map((course) => (
                            <VideoCard 
                                key={course.course_id} 
                                video={{
                                    id: course.course_id,
                                    title: course.title,
                                    instructor: course.instructor,
                                    cost: course.cost,
                                    thumbnail: course.thumbnail,
                                    lessons: course.lessons,
                                    course_id: course.course_id,
                                }} 
                                type="course" 
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CoursesPage;
